import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowRight, ArrowLeft, Loader2, MapPin, Clock, Check, ShieldCheck, Zap, CalendarDays, Upload, Paperclip,
  HelpCircle, Mail, PhoneCall, ClipboardCheck, Gift, AlertCircle,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import Footer from "@/components/Footer";
import { api, apiError } from "@/lib/api";
import WaitlistForm from "@/components/WaitlistForm";
import { trackSchedule, trackLead, trackReservationStart } from "@/lib/pixels";
import { LocalFileThumb } from "@/components/DocPreview";
import ConfirmReservationModal from "@/components/ConfirmReservationModal";

const MAX_FILES_PER_DOC = 3;
const STEPS = ["Ville", "Formule", "Mes infos"];
const STEP_HINT = ["≈ 3 min au total", "Plus que 2 étapes", "Dernière étape"];

const normCity = (c) => (c || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim().toLowerCase();

// Message d'erreur affiché sous un champ
const FieldError = ({ msg, testId }) => (msg ? (
  <p className="mt-1.5 flex items-center gap-1 text-xs font-semibold text-red-600" data-testid={testId}>
    <AlertCircle className="h-3.5 w-3.5" /> {msg}
  </p>
) : null);

// Choix Oui / Non
const YesNo = ({ value, onChange, testId, hasError }) => (
  <div className="mt-2 flex gap-2" data-testid={testId}>
    {[["Oui", true], ["Non", false]].map(([lbl, val]) => (
      <button type="button" key={lbl} onClick={() => onChange(val)} aria-pressed={value === val}
        className={`min-w-[88px] rounded-xl border px-5 py-2.5 text-sm font-bold transition-colors ${value === val ? "border-brand bg-brand/10 text-brand" : hasError ? "border-red-300 text-neutral-600" : "border-neutral-300 text-neutral-600 hover:border-neutral-400"}`}>
        {lbl}
      </button>
    ))}
  </div>
);

export default function Reserver() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const wantedCity = params.get("ville");
  // Parcours « repassage » (élève qui a raté l'examen) : formules courtes en premier.
  const retakeMode = params.get("parcours") === "repassage";
  const autoPicked = useRef(false);
  const [step, setStep] = useState(1);
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [loading, setLoading] = useState(false);

  const [cities, setCities] = useState([]);
  const [citiesError, setCitiesError] = useState(false);
  const [city, setCity] = useState(null); // {city, city_norm, remaining, year, month, month_label}
  const [offers, setOffers] = useState([]);
  const [offer, setOffer] = useState(null);

  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    has_code: null, has_neph: null, neph_number: "", ambassador_code: "",
    already_took_exam: retakeMode ? true : null,
  });
  const [showReferral, setShowReferral] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [understood, setUnderstood] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [files, setFiles] = useState({ code_result: [] });
  const set = (k) => (e) => { setForm((f) => ({ ...f, [k]: e.target.value })); setErrors((er) => ({ ...er, [k]: undefined })); };
  const setVal = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((er) => ({ ...er, [k]: undefined })); };

  // À chaque changement d'étape, on remonte en haut de page.
  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [step]);

  // Étape 1 — villes avec places accéléré (chargement résilient : 1 réessai auto)
  const loadCities = useCallback(async (attempt = 0, silent = false) => {
    if (!silent) setLoading(true);
    setCitiesError(false);
    try {
      const { data } = await api.get("/public/booking/cities");
      setCities((data.cities || []).filter((c) => c.remaining > 0));
      setCitiesError(false);
    } catch (e) {
      if (attempt < 1) {
        setTimeout(() => loadCities(attempt + 1, silent), 1200);
        return;
      }
      if (!silent) setCitiesError(true);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { loadCities(0); }, [loadCities]);

  // Rafraîchissement temps réel des compteurs de places tant que l'élève est sur l'étape « Ville ».
  useEffect(() => {
    if (step !== 1) return undefined;
    const id = setInterval(() => loadCities(0, true), 30000);
    return () => clearInterval(id);
  }, [step, loadCities]);

  const pickCity = useCallback(async (c) => {
    setCity(c); setOffer(null);
    setLoading(true);
    try {
      const { data } = await api.get("/public/booking/devis", { params: { city: c.city, year: c.year, month: c.month } });
      const list = data.offers || [];
      // En repassage : formules « idéales pour repasser » d'abord (les moins d'heures en premier), puis le reste.
      setOffers(retakeMode
        ? [...list].sort((a, b) => (b.retake_ideal === true) - (a.retake_ideal === true) || (a.retake_ideal ? (a.hours || 0) - (b.hours || 0) : 0))
        : list);
      setStep(2);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [retakeMode]);

  // Lien direct depuis l'accueil / un e-mail : /reserver?ville=Paris -> on saute l'étape ville.
  useEffect(() => {
    if (autoPicked.current || !wantedCity || !cities.length) return;
    const match = cities.find((c) => c.city_norm === normCity(wantedCity) || normCity(c.city) === normCity(wantedCity));
    autoPicked.current = true;
    if (match) pickCity(match);
  }, [cities, wantedCity, pickCity]);

  const pickOffer = (o) => {
    setOffer(o); setStep(3);
    // Début de réservation (choix d'une formule) -> InitiateCheckout
    trackReservationStart({ formula: o.title, value: o.price || 0 });
  };

  const uploadDocs = async (rid, code, list) => {
    if (!list || !list.length) return null;
    const fd = new FormData();
    fd.append("category", code);
    list.forEach((f) => fd.append("files", f));
    const { data } = await api.post(`/public/booking/reservation/${rid}/documents`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
  };

  const validate = () => {
    const er = {};
    if (!form.first_name.trim()) er.first_name = "Indique ton prénom.";
    if (!form.last_name.trim()) er.last_name = "Indique ton nom.";
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) er.email = "Cette adresse e-mail ne semble pas valide.";
    if (form.phone.replace(/\D/g, "").length < 10) er.phone = "Indique un numéro de téléphone complet (10 chiffres).";
    if (form.has_code === null) er.has_code = "Réponds par Oui ou Non.";
    if (form.has_code === true && !files.code_result.length) er.code_result = "Ajoute ton relevé de résultat du code (photo ou PDF).";
    if (form.already_took_exam === null) er.already_took_exam = "Réponds par Oui ou Non.";
    if (form.has_neph === null) er.has_neph = "Réponds par Oui ou Non.";
    if (form.has_neph === true) {
      const cleaned = form.neph_number.replace(/[\s.-]/g, "");
      if (cleaned.length !== 12 || !/^\d+$/.test(cleaned)) er.neph_number = "Le numéro NEPH doit comporter exactement 12 chiffres.";
    }
    if (!accepted) er.accepted = "Coche la case pour confirmer ta réservation.";
    return er;
  };

  const submit = async (e) => {
    e.preventDefault();
    const er = validate();
    setErrors(er);
    const keys = Object.keys(er);
    if (keys.length) {
      toast.error(er[keys[0]]);
      const el = document.querySelector(`[data-field="${keys[0]}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    // Formulaire valide : on explique les 2 points importants avant de valider.
    setUnderstood(false);
    setSubmitError("");
    setConfirmOpen(true);
  };

  const doSubmit = async () => {
    if (!understood) return;
    setSubmitError("");
    setSaving(true);
    try {
      const { data } = await api.post("/public/booking/reserve", {
        city: city.city, year: city.year, month: city.month,
        offer_id: offer.offer_id,
        gearbox: offer.gearbox || null,
        has_code: form.has_code,
        already_took_exam: form.already_took_exam,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        ambassador_code: form.ambassador_code.trim() || null,
        accepted_terms: accepted,
        origin_url: typeof window !== "undefined" ? window.location.href : "",
      });
      const rid = data.reservation_id;
      // Google Ads : conversion « Envoi de formulaire de lead » (1 €)
      trackLead(rid, "reserver");
      // Enregistre le NEPH puis dépose le relevé du code (si l'élève a le code).
      try {
        await api.post(`/public/booking/reservation/${rid}/neph`, {
          has_neph: form.has_neph, neph_number: form.has_neph ? form.neph_number.trim() : "",
        });
        let complete = form.has_code !== true;
        if (form.has_code === true) {
          const last = await uploadDocs(rid, "code_result", files.code_result);
          complete = !!last?.docs_complete;
        }
        // Fin de réservation (dossier complet) -> événement Meta « Schedule »
        if (complete) {
          trackSchedule({ reservationId: rid, formula: offer.title, city: city.city });
        }
      } catch (upErr) {
        // Réservation créée : si un envoi échoue, l'élève complètera sur la page suivante.
        toast.message("Place réservée ! Tu pourras ajouter ton relevé sur la page suivante.");
      }
      setConfirmOpen(false);
      navigate(`/reservation/${rid}`);
    } catch (e2) {
      const msg = apiError(e2.response?.data?.detail) || "Une erreur est survenue, réessaie.";
      setSubmitError(msg);
      toast.error(msg);
    } finally { setSaving(false); }
  };

  const field = (k) => `mt-1 w-full rounded-xl border px-4 py-3 text-base outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 sm:text-sm ${errors[k] ? "border-red-400 bg-red-50/40" : "border-neutral-300"}`;
  const card = "group w-full rounded-2xl border border-neutral-200 bg-white p-4 text-left shadow-sm transition-colors hover:border-brand hover:bg-brand/5 focus:outline-none focus:ring-2 focus:ring-brand/30";
  const progress = Math.round((step / STEPS.length) * 100);

  return (
    <div className="flex min-h-screen flex-col bg-[#FAFAFA]">
      <header className="sticky top-0 z-30 border-b border-neutral-200 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-5 py-3">
          <Link to="/"><Logo className="h-10" /></Link>
          <Link to="/contact" data-testid="reserver-help-link" className="inline-flex items-center gap-1.5 text-sm font-semibold text-neutral-600 hover:text-brand">
            <HelpCircle className="h-4 w-4" /> {"Besoin d'aide ?"}
          </Link>
        </div>
        {/* Barre de progression */}
        <div className="h-1 w-full bg-neutral-100"><div className="h-1 bg-brand transition-all duration-500" style={{ width: `${progress}%` }} data-testid="reserve-progress" /></div>
      </header>

      <main className="mx-auto w-full max-w-4xl flex-1 px-5 py-7">
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700"><Zap className="h-4 w-4" /> Permis accéléré · 0 € pour toi</span>
          <span className="text-xs font-semibold text-neutral-500" data-testid="reserve-step-hint">Étape {step} sur {STEPS.length} · {STEP_HINT[step - 1]}</span>
        </div>

        {/* Stepper */}
        <ol className="mt-4 flex flex-wrap gap-2" data-testid="reserve-steps">
          {STEPS.map((s, i) => {
            const n = i + 1;
            const done = step > n;
            const current = step === n;
            return (
              <li key={s}>
                <button type="button" disabled={!done} onClick={() => done && setStep(n)}
                  className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-bold ${current ? "bg-brand text-white" : done ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200" : "bg-neutral-100 text-neutral-500"}`}>
                  <span className="grid h-5 w-5 place-items-center rounded-full bg-white/30">{done ? <Check className="h-3.5 w-3.5" /> : n}</span>{s}
                </button>
              </li>
            );
          })}
        </ol>

        {step > 1 && (
          <button onClick={() => setStep(step - 1)} data-testid="reserve-back" className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-neutral-500 hover:text-brand">
            <ArrowLeft className="h-4 w-4" /> Retour
          </button>
        )}

        <div className="mt-4">
          {loading && <div className="grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>}

          {/* Étape 1 : ville */}
          {!loading && step === 1 && (
            <div data-testid="step-city">
              <h1 className="text-2xl font-black tracking-tight text-neutral-900 sm:text-3xl">Où veux-tu passer ton permis ?</h1>
              <p className="mt-1.5 text-sm text-neutral-600">Choisis une ville : ce sont celles où il reste des places en accéléré.</p>
              <div data-testid="current-month-banner" className="mt-4 flex items-start gap-2.5 rounded-2xl border border-brand/20 bg-brand/5 px-4 py-3 text-sm text-neutral-700">
                <CalendarDays className="mt-0.5 h-5 w-5 shrink-0 text-brand" />
                <p>
                  Places pour <strong className="text-neutral-900">{cities[0]?.month_label || "le mois en cours"}</strong>.{" "}
                  {"Le mois suivant ouvrira bientôt."}
                </p>
              </div>
              {citiesError ? (
                <div className="mt-4 rounded-2xl border border-neutral-200 bg-white p-6 text-center text-sm text-neutral-600">
                  {"Impossible de charger les villes pour le moment."}{" "}
                  <button type="button" onClick={() => loadCities(0)} className="font-bold text-brand hover:underline">Réessayer</button>
                </div>
              ) : cities.length === 0 ? (
                <div className="mt-4 space-y-5">
                  <div className="rounded-2xl border border-neutral-200 bg-white p-6 text-center text-neutral-600">
                    <p className="font-bold text-neutral-900">Toutes les places de ce mois sont prises.</p>
                    <p className="mt-1 text-sm">{"Laisse tes coordonnées : on te prévient dès qu'une place se libère."}</p>
                  </div>
                  <WaitlistForm />
                </div>
              ) : (
                <>
                  <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    {cities.map((c) => (
                      <button key={c.city_norm} onClick={() => pickCity(c)} data-testid={`city-${c.city_norm}`} className={card}>
                        <div className="flex items-center justify-between gap-3">
                          <span className="flex items-center gap-2 text-base font-bold text-neutral-900"><MapPin className="h-4 w-4 text-brand" /> {c.city}</span>
                          <span className="flex items-center gap-2">
                            <span
                              data-testid={`city-remaining-${c.city_norm}`}
                              className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${c.remaining <= 2 ? "bg-red-50 text-red-600" : "bg-emerald-50 text-emerald-700"}`}
                            >
                              {c.remaining <= 2 ? `Plus que ${c.remaining} place${c.remaining > 1 ? "s" : ""} !` : `${c.remaining} places`}
                            </span>
                            <ArrowRight className="h-4 w-4 text-neutral-300 transition-colors group-hover:text-brand" />
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                  <div className="mt-5">
                    {!showWaitlist ? (
                      <button
                        type="button"
                        onClick={() => setShowWaitlist(true)}
                        data-testid="open-waitlist"
                        className="text-sm font-semibold text-brand underline-offset-2 hover:underline"
                      >
                        {"Pas de ville proche de chez toi ? Préviens-moi →"}
                      </button>
                    ) : (
                      <WaitlistForm />
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Étape 2 : formules */}
          {!loading && step === 2 && (
            <div data-testid="step-offers">
              <h1 className="text-2xl font-black tracking-tight text-neutral-900 sm:text-3xl">{retakeMode ? "Choisis ta formule pour repasser" : "Choisis ta formule"}</h1>
              {retakeMode && (
                <p className="mt-2 rounded-xl bg-amber-50 px-4 py-3 text-xs leading-relaxed text-amber-900 ring-1 ring-amber-200" data-testid="offers-retake-tip">
                  {"🔁 Pour repasser vite, on te recommande une formule courte (au moins 10 h en 2 semaines) : entraînement sur le parcours d'examen, date rapide et accompagnement inclus. Elles sont affichées en premier."}
                </p>
              )}
              <p className="mt-1.5 text-sm text-neutral-600"><MapPin className="mr-1 inline h-4 w-4 text-brand" />{city?.city} · {city?.month_label}</p>
              <p className="mt-3 flex items-start gap-2 rounded-xl bg-white px-4 py-3 text-xs leading-relaxed text-neutral-600 ring-1 ring-neutral-200" data-testid="offers-explainer">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                <span>{"Les prix sont ceux fixés par nos auto-écoles partenaires agréées. Tu découvres le nom et le téléphone de ton auto-école juste après ta réservation. Tu paies ta formation directement à l'auto-école, rien ici."}</span>
              </p>
              {offers.length === 0 ? (
                <div className="mt-4 rounded-2xl border border-neutral-200 bg-white p-6 text-center text-neutral-500">
                  {"Plus aucune formule disponible ici ce mois-ci."}{" "}
                  <button type="button" onClick={() => setStep(1)} className="font-bold text-brand hover:underline">Choisir une autre ville</button>
                </div>
              ) : (
                <div className="mt-4 space-y-3">
                  {offers.map((o) => (
                    <button key={o.offer_id} onClick={() => pickOffer(o)} data-testid={`offer-${o.offer_id}`} className={card}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-base font-bold text-neutral-900">{o.title}</p>
                          <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs">
                            {o.hours ? <span className="inline-flex items-center gap-1 rounded-full bg-neutral-100 px-2.5 py-1 font-semibold text-neutral-700"><Clock className="h-3.5 w-3.5" /> {o.hours} h de conduite</span> : null}
                            {o.gearbox ? <span className="rounded-full bg-neutral-100 px-2.5 py-1 font-semibold text-neutral-700">{/^bo[iî]te|deux/i.test(o.gearbox) ? o.gearbox : `Boîte ${o.gearbox}`}</span> : null}
                            {o.code_included ? <span className="rounded-full bg-emerald-50 px-2.5 py-1 font-bold text-emerald-700">Code inclus</span> : null}
                            {o.retake_ideal && retakeMode ? <span className="rounded-full bg-amber-100 px-2.5 py-1 font-bold text-amber-800" data-testid={`offer-retake-badge-${o.offer_id}`}>🔁 Idéal pour repasser</span> : null}
                          </div>
                          {(o.options || []).length > 0 && <p className="mt-2 text-xs text-neutral-500">{o.options.join(" · ")}</p>}
                        </div>
                        <div className="shrink-0 text-right">
                          <p className="text-xl font-black text-brand">{o.price != null ? `${o.price} €` : "—"}</p>
                          <span className="mt-1 inline-flex items-center gap-1 rounded-lg bg-brand px-3 py-1.5 text-xs font-bold text-white">Choisir <ArrowRight className="h-3.5 w-3.5" /></span>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Étape 3 : formulaire */}
          {!loading && step === 3 && offer && (
            <form onSubmit={submit} noValidate data-testid="step-form">
              <h1 className="text-2xl font-black tracking-tight text-neutral-900 sm:text-3xl">{"Plus qu'une étape : tes infos"}</h1>
              <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-xl bg-white px-4 py-3 text-sm text-neutral-600 ring-1 ring-neutral-200" data-testid="form-recap">
                <span><strong className="text-neutral-900">{offer.title}</strong> · {city?.city} · {city?.month_label}</span>
                <span className="font-black text-brand">{offer.price != null ? `${offer.price} €` : ""}</span>
              </div>

              <div className="mt-5 space-y-5">
                {/* 1. Coordonnées */}
                <section className="rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm sm:p-6">
                  <h2 className="text-base font-black text-neutral-900">1. Tes coordonnées</h2>
                  <p className="mt-0.5 text-xs text-neutral-500">{"Pour t'envoyer les coordonnées de ton auto-école. Pas de pub, promis."}</p>
                  <div className="mt-4 grid gap-4 sm:grid-cols-2">
                    <div data-field="first_name"><label htmlFor="r-fn" className="text-sm font-semibold text-neutral-700">Prénom</label><input id="r-fn" autoComplete="given-name" value={form.first_name} onChange={set("first_name")} data-testid="r-firstname" className={field("first_name")} /><FieldError msg={errors.first_name} /></div>
                    <div data-field="last_name"><label htmlFor="r-ln" className="text-sm font-semibold text-neutral-700">Nom</label><input id="r-ln" autoComplete="family-name" value={form.last_name} onChange={set("last_name")} data-testid="r-lastname" className={field("last_name")} /><FieldError msg={errors.last_name} /></div>
                    <div data-field="email"><label htmlFor="r-em" className="text-sm font-semibold text-neutral-700">E-mail</label><input id="r-em" type="email" inputMode="email" autoComplete="email" placeholder="prenom@exemple.fr" value={form.email} onChange={set("email")} data-testid="r-email" className={field("email")} /><FieldError msg={errors.email} testId="err-email" /></div>
                    <div data-field="phone"><label htmlFor="r-ph" className="text-sm font-semibold text-neutral-700">Téléphone</label><input id="r-ph" type="tel" inputMode="tel" autoComplete="tel" placeholder="06 12 34 56 78" value={form.phone} onChange={set("phone")} data-testid="r-phone" className={field("phone")} /><FieldError msg={errors.phone} testId="err-phone" /></div>
                  </div>
                </section>

                {/* 2. Situation */}
                <section className="rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm sm:p-6">
                  <h2 className="text-base font-black text-neutral-900">2. Ta situation</h2>
                  <p className="mt-0.5 text-xs text-neutral-500">{"3 questions rapides pour préparer ton inscription."}</p>

                  <div className="mt-4" data-field="has_code">
                    <span className="text-sm font-semibold text-neutral-700">As-tu déjà le code de la route ?</span>
                    <YesNo value={form.has_code} onChange={(v) => { setVal("has_code", v); setErrors((er) => ({ ...er, code_result: undefined })); }} testId="r-hascode" hasError={!!errors.has_code} />
                    <FieldError msg={errors.has_code} testId="err-hascode" />
                    {form.has_code === true && (
                      <div data-field="code_result" className={`mt-3 rounded-xl border p-4 ${errors.code_result ? "border-red-300 bg-red-50/40" : "border-neutral-200 bg-neutral-50/60"}`} data-testid="r-code-result">
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <p className="flex items-center gap-2 text-sm font-bold text-neutral-800"><Paperclip className="h-4 w-4 text-brand" /> {"Ton relevé de résultat du code"}</p>
                            <p className="mt-0.5 text-xs text-neutral-500">{"Une photo nette ou le PDF reçu après l'examen (JPEG, PNG ou PDF, 15 Mo max)."}</p>
                            {files.code_result.length > 0 && (
                              <p className="mt-1 flex items-center gap-1 text-xs font-bold text-emerald-600"><Check className="h-3.5 w-3.5" /> {files.code_result.length}/{MAX_FILES_PER_DOC} fichier(s) ajouté(s)</p>
                            )}
                          </div>
                          <label data-testid="r-file-code_result" className={`inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-lg bg-brand px-3.5 py-2 text-xs font-bold text-white hover:bg-brand-hover ${files.code_result.length >= MAX_FILES_PER_DOC ? "pointer-events-none opacity-40" : ""}`}>
                            <Upload className="h-3.5 w-3.5" /> Ajouter
                            <input type="file" multiple accept="image/*,application/pdf"
                              disabled={files.code_result.length >= MAX_FILES_PER_DOC}
                              onChange={(e) => {
                                const picked = Array.from(e.target.files || []);
                                e.target.value = "";
                                setErrors((er) => ({ ...er, code_result: undefined }));
                                setFiles((s) => {
                                  let list = [...(s.code_result || []), ...picked];
                                  if (list.length > MAX_FILES_PER_DOC) {
                                    toast.error(`${MAX_FILES_PER_DOC} fichiers maximum.`);
                                    list = list.slice(0, MAX_FILES_PER_DOC);
                                  }
                                  return { ...s, code_result: list };
                                });
                              }}
                              className="hidden" />
                          </label>
                        </div>
                        {files.code_result.length > 0 && (
                          <div className="mt-2 grid grid-cols-3 gap-2 sm:grid-cols-4" data-testid="r-thumbs-code_result">
                            {files.code_result.map((f, i) => (
                              <LocalFileThumb key={`${f.name}-${f.size}-${f.lastModified}-${i}`} file={f} testId={`r-thumb-code_result-${i}`}
                                onRemove={() => setFiles((s) => ({ ...s, code_result: s.code_result.filter((_, j) => j !== i) }))} />
                            ))}
                          </div>
                        )}
                        <FieldError msg={errors.code_result} testId="err-code-result" />
                      </div>
                    )}
                    {form.has_code === false && (
                      <p className="mt-2 text-xs text-neutral-500">{"Pas de souci : l'auto-école t'accompagnera pour le code."}</p>
                    )}
                  </div>

                  <div className="mt-5 border-t border-neutral-100 pt-5" data-field="already_took_exam">
                    <span className="text-sm font-semibold text-neutral-700">{"As-tu déjà passé l'examen pratique (la conduite) ?"}</span>
                    <YesNo value={form.already_took_exam} onChange={(v) => setVal("already_took_exam", v)} testId="r-tookexam" hasError={!!errors.already_took_exam} />
                    <FieldError msg={errors.already_took_exam} testId="err-tookexam" />
                    {form.already_took_exam === true && (
                      <p className="mt-2 text-xs text-neutral-500">{"Noté : ton auto-école saura que c'est un repassage et préparera ton entraînement sur le parcours d'examen."}</p>
                    )}
                  </div>

                  <div className="mt-5 border-t border-neutral-100 pt-5" data-field="has_neph">
                    <span className="text-sm font-semibold text-neutral-700">As-tu déjà un numéro NEPH ?</span>
                    <details className="group mt-1" data-testid="neph-help">
                      <summary className="inline-flex cursor-pointer list-none items-center gap-1 text-xs font-semibold text-brand hover:underline"><HelpCircle className="h-3.5 w-3.5" /> {"C'est quoi ?"}</summary>
                      <p className="mt-1.5 rounded-lg bg-brand/5 px-3 py-2 text-xs leading-relaxed text-neutral-600">{"C'est ton numéro de dossier permis (12 chiffres), créé lors de ta première inscription au permis. Il figure sur ton compte ANTS ou ta convocation au code. Tu n'en as pas ? Réponds Non, l'auto-école s'en occupera."}</p>
                    </details>
                    <YesNo value={form.has_neph} onChange={(v) => setVal("has_neph", v)} testId="r-hasneph" hasError={!!errors.has_neph} />
                    <FieldError msg={errors.has_neph} testId="err-hasneph" />
                    {form.has_neph === true && (
                      <div data-field="neph_number">
                        <input value={form.neph_number} onChange={set("neph_number")} inputMode="numeric" placeholder="Ex. 051227308989 (12 chiffres)" data-testid="r-neph" className={field("neph_number")} />
                        <FieldError msg={errors.neph_number} testId="err-neph" />
                      </div>
                    )}
                  </div>
                </section>

                {/* Parrainage (replié pour alléger) */}
                <div>
                  {!showReferral && !form.ambassador_code ? (
                    <button type="button" onClick={() => setShowReferral(true)} data-testid="r-referral-toggle" className="inline-flex items-center gap-1.5 text-sm font-semibold text-neutral-600 hover:text-brand">
                      <Gift className="h-4 w-4 text-brand" /> {"Quelqu'un t'a recommandé ? Ajoute son code"}
                    </button>
                  ) : (
                    <div className="rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm">
                      <label htmlFor="r-amb" className="text-sm font-semibold text-neutral-700">Code parrainage (facultatif)</label>
                      <input id="r-amb" value={form.ambassador_code} onChange={set("ambassador_code")} placeholder="PP-XXXX" data-testid="r-ambassador" className={field("ambassador_code")} />
                    </div>
                  )}
                </div>

                {/* Et après ? */}
                <section className="rounded-2xl border border-brand/20 bg-brand/5 p-5" data-testid="form-next-steps">
                  <h2 className="text-sm font-black text-neutral-900">Et après, comment ça se passe ?</h2>
                  <ol className="mt-3 space-y-2.5 text-sm text-neutral-700">
                    <li className="flex gap-3"><span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-white text-brand ring-1 ring-brand/20"><Mail className="h-3.5 w-3.5" /></span><span>{"Tu reçois tout de suite un e-mail avec le nom, l'adresse et le téléphone de ton auto-école."}</span></li>
                    <li className="flex gap-3"><span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-white text-brand ring-1 ring-brand/20"><PhoneCall className="h-3.5 w-3.5" /></span><span><strong>{"Tu l'appelles sous 2 jours"}</strong>{" pour fixer ton rendez-vous d'inscription."}</span></li>
                    <li className="flex gap-3"><span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-white text-brand ring-1 ring-brand/20"><ClipboardCheck className="h-3.5 w-3.5" /></span><span>{"Tu finalises ton inscription et tu règles ta formation directement avec elle."}</span></li>
                  </ol>
                </section>

                <div data-field="accepted">
                  <label className={`flex items-start gap-3 rounded-xl p-1 text-sm text-neutral-600 ${errors.accepted ? "ring-1 ring-red-300" : ""}`}>
                    <input type="checkbox" checked={accepted} onChange={(e) => { setAccepted(e.target.checked); setErrors((er) => ({ ...er, accepted: undefined })); }} data-testid="r-accept" className="mt-0.5 h-5 w-5 shrink-0 rounded border-neutral-300 text-brand focus:ring-brand" />
                    <span>{"J'accepte les "}<Link to="/cgv" className="font-semibold text-brand hover:underline">conditions</Link>{" et la "}<Link to="/confidentialite" className="font-semibold text-brand hover:underline">politique de confidentialité</Link>.</span>
                  </label>
                  <FieldError msg={errors.accepted} testId="err-accept" />
                </div>
              </div>

              <button type="submit" disabled={saving} data-testid="r-submit"
                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-brand px-6 py-4 text-base font-bold text-white shadow-lg shadow-brand/20 hover:bg-brand-hover disabled:opacity-60">
                {saving ? <><Loader2 className="h-5 w-5 animate-spin" /> Envoi en cours…</> : <>{"Réserver ma place gratuitement"} <ArrowRight className="h-5 w-5" /></>}
              </button>
              <p className="mt-3 flex items-center justify-center gap-1.5 text-center text-xs text-neutral-500"><ShieldCheck className="h-3.5 w-3.5" /> 0 € · sans paiement en ligne · 1 place par personne</p>
            </form>
          )}
        </div>
      </main>
      <Footer />

      {confirmOpen && (
        <ConfirmReservationModal
          understood={understood} setUnderstood={setUnderstood}
          saving={saving} error={submitError}
          onConfirm={doSubmit} onClose={() => setConfirmOpen(false)}
        />
      )}
    </div>
  );
}
